// VisitCard (Domain/Schedule; was ScheduleViews' VisitCard, BL-189): one visit, as Schedule's map shows
// the one picked: when, its state as a word, who for, where, what for and who is going, the lead first,
// each by their pen. The state is drawn as DESIGN.md §2 says: muted, a dashed underline while it waits,
// ink while it's under way, red for a problem; nobody on it is red too. Notes under it say more (why it's
// paused, running late). With no visit (nothing picked yet) it says so, between dashed rules. It draws
// no buttons: what can be done next is the screen's. Draws from ready-to-show words (VisitCardData, as
// dispatch.board's visit gives them). Reads only --visitcard-*; PenKey reads its own.
import type { ReactNode } from 'react';
import { PenKey } from '../PenKey/PenKey.tsx';
import type { Pen } from '../pen.ts';
import './VisitCard.css';

/** A state's word: muted for kind and state, a dashed underline for waiting, red only for a problem. */
export type VisitWordKind = 'muted' | 'wait' | 'bad' | 'now';

export interface VisitCardData {
  /** "8–10 a.m.", or with its day when it isn't the day shown: "Thu, 8–10 a.m." */
  when: string;
  customer: string;
  address?: string | null;
  /** What it's for, in the customer's words. */
  work: string;
  /** "#24580" */
  job?: string | null;
  /** Its state as a word, drawn as DESIGN.md §2 says: muted, waiting, under way, or red for a problem. */
  state: { label: string; kind: VisitWordKind };
  /** Who is going, the lead first; none: nobody yet, which is a problem. */
  techs: { name: string; pen?: number | null; note?: string | null }[];
  /** More to know, each a line: why it's paused, running late, a licence nobody on it holds. */
  notes?: { text: string; kind?: VisitWordKind }[];
}

export interface VisitCardProps {
  /** The visit; none: nothing picked yet, and `empty` says how to pick one. */
  visit: VisitCardData | null;
  /** With no visit: what to do to see one ("Tap a pin. The visit shows here…"). */
  empty?: ReactNode;
}

const Word = ({ kind, children }: { kind: VisitWordKind; children: ReactNode }) => (
  <span className="fl-visitcard__word" data-kind={kind}>
    {children}
  </span>
);

/** One visit: when, its state, who for, where, what for, and who is going. */
export function VisitCard({ visit: v, empty = 'No visit picked.' }: VisitCardProps) {
  if (!v) return <p className="fl-visitcard__empty">{empty}</p>;
  const who: ReactNode = v.techs.length ? (
    <span className="fl-visitcard__techs">
      {v.techs.map((t, i) => (
        <span key={`${t.name}-${i}`} className="fl-visitcard__tech">
          <PenKey pen={(t.pen ?? null) as Pen | null}>
            <span className="fl-visitcard__name">{t.name}</span>
          </PenKey>
          {t.note && <span className="fl-visitcard__muted">, {t.note}</span>}
        </span>
      ))}
    </span>
  ) : (
    <Word kind="bad">No one yet</Word>
  );
  return (
    <article className="fl-visitcard" aria-label={`Visit for ${v.customer}`}>
      <p className="fl-visitcard__head">
        <b>{v.when}</b>
        <Word kind={v.state.kind}>{v.state.label}</Word>
      </p>
      <h3 className="fl-visitcard__customer">{v.customer}</h3>
      {v.address && <p className="fl-visitcard__address">{v.address}</p>}
      <dl className="fl-visitcard__facts">
        <dt>Work</dt>
        <dd>
          {v.work}
          {v.job && <span className="fl-visitcard__muted"> {v.job}</span>}
        </dd>
        <dt>Who</dt>
        <dd>{who}</dd>
      </dl>
      {(v.notes ?? []).map((n, i) => (
        <p key={i} className="fl-visitcard__note">
          <Word kind={n.kind ?? 'muted'}>{n.text}</Word>
        </p>
      ))}
    </article>
  );
}
